import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, resolveDirective as _resolveDirective, openBlock as _openBlock, createElementBlock as _createElementBlock, withDirectives as _withDirectives, withCtx as _withCtx, resolveComponent as _resolveComponent, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createBlock as _createBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = (n: any) => (_pushScopeId("data-v-456d6e94"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "data-security-zone__channel-inner" }

import { ref, computed, onMounted } from 'vue'
import TimeRangeSelect from '@/views/dashboard/_components/time-range-select/time-range-select.vue'
import { TimeRange } from '@/views/dashboard/_components/time-range-select/time-range.enum'
import { getTimeRangeByEnum } from '@/utils/time'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useReflectiveInjector } from '@tanbo/vue-di-plugin'
import Card from '@/views/dashboard/_components/card/card.vue'
import StatisticsCards from '@/views/dashboard/_components/statistics-cards/statistics-cards.vue'
import ChannelDistributionChart from './_components/channel-distribution-chart.vue'
import SensitiveOutboundEvents from './_components/sensitive-outbound-events.vue'
import { ChannelDistributionData } from '../types'
import { SensitiveService, SensitiveWordsFileOutgoingLogRow } from '@/views/dashboard/data-safe/anti-external-control/service'
import { PermissionFe, ServicePermissionService } from '@/services/service-permission.service'
import {
  SensitivefilesendHomepageStatService,
  ChannelDistribution,
  ResponseData,
} from '@/views/dashboard/overview/service'

const PREVIEW_LIMIT = 5


export default /*#__PURE__*/_defineComponent({
  __name: 'data-security-zone',
  setup(__props) {

const router = useRouter()
const { t, locale } = useI18n()

/** 让卡片主体成为纵向 flex 容器，图表区可 flex:1 占满剩余高度 */
const channelCardBodyStyle = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
  overflow: 'hidden',
} as const

const injector = useReflectiveInjector([
  SensitiveService,
  SensitivefilesendHomepageStatService,
])
const sensitiveService = injector.get(SensitiveService)
const servicePermissionService = injector.get(ServicePermissionService)
const homepageStatService = injector.get(SensitivefilesendHomepageStatService)
const hasOutboundLogPermission = servicePermissionService.getHasPermission(PermissionFe.dlp_control)

/** 与接口约定一致：1-网络协议 2-通信软件 3-网盘 4-传播设备 99-其他 */
const CHANNEL_ORDER = ['1', '2', '3', '4', '99'] as const

function channelTypeLabelKey(type: string): string {
  const map: Record<string, string> = {
    '1': 'overviewZone.channelTypeNetwork',
    '2': 'overviewZone.channelTypeIm',
    '3': 'overviewZone.channelTypeNetdisk',
    '4': 'overviewZone.channelTypeDevice',
    '99': 'overviewZone.channelTypeOther',
  }
  return map[type] || 'overviewZone.channelTypeOther'
}

function mapChannelDistributionToChart(
  rows: ChannelDistribution[] | undefined,
): ChannelDistributionData {
  const byType = new Map<string, ChannelDistribution>()
  for (const r of rows || []) {
    byType.set(String(r.channel_type), r)
  }
  const categories: string[] = []
  const allowed: number[] = []
  const blocked: number[] = []
  for (const type of CHANNEL_ORDER) {
    const row = byType.get(type)
    categories.push(t(channelTypeLabelKey(type)))
    allowed.push(row?.allowed_count ?? 0)
    blocked.push(row?.blocked_count ?? 0)
  }
  return {
    categories,
    series: [
      { name: t('outboundControlLog.sendStatusReleased'), values: allowed },
      { name: t('outboundControlLog.sendStatusBlocked'), values: blocked },
    ],
  }
}

const summaryData = ref({
  outboundCount: 0,
  fileCount: 0,
  sensitiveFileCount: 0,
})

const statisticsItems = computed(() => {
  void locale.value
  return [
    { label: t('overviewZone.metricOutbound24h'), value: summaryData.value.outboundCount, span: 8 },
    { label: t('overviewZone.metricFileCount'), value: summaryData.value.fileCount, span: 8 },
    { label: t('overviewZone.metricSensitiveCount'), value: summaryData.value.sensitiveFileCount, span: 8 },
  ]
})

const channelDistributionRaw = ref<ChannelDistribution[]>([])
const chartData = computed<ChannelDistributionData>(() => {
  void locale.value
  return mapChannelDistributionToChart(channelDistributionRaw.value)
})

const statLoading = ref(false)
const channelTimeRange = ref<TimeRange>(TimeRange.DAYS_30)

function onChannelTimeRangeChange() {
  loadHomepageStat()
}

const sendRecords = ref<SensitiveWordsFileOutgoingLogRow[]>([])
const sendLoading = ref(false)

function loadHomepageStat() {
  if (!hasOutboundLogPermission) {
    summaryData.value = { outboundCount: 0, fileCount: 0, sensitiveFileCount: 0 }
    channelDistributionRaw.value = []
    return
  }
  statLoading.value = true
  const [startSec, endSec] = getTimeRangeByEnum(channelTimeRange.value)
  const end = endSec ?? Math.floor(Date.now() / 1000)
  const start = startSec ?? end - 24 * 60 * 60
  homepageStatService
    .getSensitivefilesendHomepageStat({
      start_time: String(start),
      end_time: String(end),
    })
    .then(res => {
      const raw = res as ResponseData & { data?: ResponseData }
      const payload: ResponseData = raw.data ?? raw
      const overview = payload.overview
      summaryData.value = {
        outboundCount: overview?.total_send_count ?? 0,
        fileCount: overview?.total_file_count ?? 0,
        sensitiveFileCount: overview?.sensitive_file_count ?? 0,
      }
      channelDistributionRaw.value = payload.channel_distribution || []
    })
    .catch(() => {
      summaryData.value = { outboundCount: 0, fileCount: 0, sensitiveFileCount: 0 }
      channelDistributionRaw.value = []
    })
    .finally(() => {
      statLoading.value = false
    })
}

function loadSendFileRecords() {
  if (!hasOutboundLogPermission) {
    sendRecords.value = []
    return
  }
  sendLoading.value = true
  sensitiveService
    .getSensitiveWordsFileOutgoingLog({
      page: 1,
      limit: PREVIEW_LIMIT,
      group_id: '-1'
    })
    .then(res => {
      sendRecords.value = res.data || []
    })
    .catch(() => {
      sendRecords.value = []
    })
    .finally(() => {
      sendLoading.value = false
    })
}

function handleViewMoreChannel() {
  router.push('/dashboard/sensitive-file-scanning')
}

function handleViewMoreEvents() {
  router.push('/dashboard/sensitive-file-scanning')
}

onMounted(() => {
  loadHomepageStat()
  loadSendFileRecords()
})

return (_ctx: any,_cache: any) => {
  const _component_el_col = _resolveComponent("el-col")!
  const _component_el_tag = _resolveComponent("el-tag")!
  const _component_el_row = _resolveComponent("el-row")!
  const _directive_loading = _resolveDirective("loading")!

  return (_openBlock(), _createBlock(Card, {
    size: "first",
    "background-color": "var(--cm-color-card-bg)",
    border: "",
    title: _ctx.$t('overviewZone.dataSecurityTitle'),
    description: _ctx.$t('overviewZone.dataSecurityDesc')
  }, {
    default: _withCtx(() => [
      _createVNode(_component_el_row, {
        gutter: 16,
        class: "data-security-zone__row"
      }, {
        default: _withCtx(() => [
          _createVNode(_component_el_col, {
            span: 12,
            class: "data-security-zone__col"
          }, {
            default: _withCtx(() => [
              _createVNode(Card, {
                size: "second",
                title: _ctx.$t('overviewZone.channelDistributionTitle'),
                "body-style": channelCardBodyStyle
              }, {
                headerRight: _withCtx(() => [
                  _createVNode(TimeRangeSelect, {
                    modelValue: channelTimeRange.value,
                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((channelTimeRange).value = $event)),
                    onChange: onChannelTimeRangeChange
                  }, null, 8, ["modelValue"])
                ]),
                default: _withCtx(() => [
                  _withDirectives((_openBlock(), _createElementBlock("div", _hoisted_1, [
                    _createVNode(StatisticsCards, {
                      items: statisticsItems.value,
                      style: {"margin-bottom":"0"}
                    }, null, 8, ["items"]),
                    _createVNode(ChannelDistributionChart, { data: chartData.value }, null, 8, ["data"])
                  ])), [
                    [_directive_loading, statLoading.value]
                  ])
                ]),
                _: 1
              }, 8, ["title"])
            ]),
            _: 1
          }),
          _createVNode(_component_el_col, {
            span: 12,
            class: "data-security-zone__col"
          }, {
            default: _withCtx(() => [
              _createVNode(Card, {
                size: "second",
                title: _ctx.$t('overviewZone.sensitiveOutboundLatest'),
                style: {"height":"100%","box-sizing":"border-box"},
                "on-view-more": handleViewMoreEvents
              }, {
                titleAfter: _withCtx(() => [
                  _createVNode(_component_el_tag, {
                    size: "small",
                    type: "primary"
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(_ctx.$t('common.rankTagTop5')), 1)
                    ]),
                    _: 1
                  })
                ]),
                default: _withCtx(() => [
                  _createVNode(SensitiveOutboundEvents, {
                    records: sendRecords.value,
                    loading: sendLoading.value
                  }, null, 8, ["records", "loading"])
                ]),
                _: 1
              }, 8, ["title"])
            ]),
            _: 1
          })
        ]),
        _: 1
      })
    ]),
    _: 1
  }, 8, ["title", "description"]))
}
}

})